import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  X,
  RefreshCw,
  Rocket,
  Layers,
  Loader2,
  CalendarDays,
  Filter,
  AlertTriangle,
  Tag,
  Play,
  FileText,
  Database,
  SlidersHorizontal,
  ListChecks,
  CheckCircle2,
  Clock,
  History,
} from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { JumpStartServiceResponseDTO, JumpStartLogEntryDTO } from '../Types/AdminType';
import { runJumpStartService } from '../Services/AdminService';

const HISTORY_STORAGE_KEY = 'jumpstart-run-history';
const MAX_HISTORY_PER_SERVICE = 20;

type ConfigType = 'MASTER' | 'FILTER' | 'SCORE' | 'MENU';
type RunStatus = 'SUCCESS' | 'FAILED';

interface ServiceData {
  id: string;
  name: string;
  label: string;
  type: ConfigType;
  lastRun: string;
}

interface RunLog {
  timestamp: string;
  level: string;
  message: string;
}

interface RunRecord {
  id: string;
  name: string;
  label: string;
  ranAt: string;
  inserted: number;
  skipped: number;
  durationMs: number;
  status: RunStatus;
  message?: string;
  logs: RunLog[];
}

const SERVICES: ServiceData[] = [
  { id: '1', name: 'Role Levels Config', label: 'RoleLevel', type: 'MASTER', lastRun: '—' },
  { id: '2', name: 'Target Role Config', label: 'TargetRole', type: 'MASTER', lastRun: '—' },
  { id: '3', name: 'Architecture Experience Config', label: 'ArchitectureExperience', type: 'MASTER', lastRun: '—' },
  { id: '4', name: 'OwnerShip Level Config', label: 'OwnerShipLevel', type: 'MASTER', lastRun: '—' },
  { id: '5', name: 'Hiring Authority Config', label: 'HiringAuthority', type: 'MASTER', lastRun: '—' },
  { id: '6', name: 'Product Scale Config', label: 'ProductScale', type: 'MASTER', lastRun: '—' },
  { id: '7', name: 'Business Impact Type Config', label: 'BusinessImpactType', type: 'MASTER', lastRun: '—' },
  { id: '8', name: 'Decision Speed Config', label: 'DecisionSpeed', type: 'MASTER', lastRun: '—' },
  { id: '9', name: 'Reputation Signals Config', label: 'ReputationSignals', type: 'MASTER', lastRun: '—' },
  { id: '10', name: 'Work Style Config', label: 'WorkStyle', type: 'MASTER', lastRun: '—' },
  { id: '11', name: 'Work Flexibility Config', label: 'WorkFlexibility', type: 'MASTER', lastRun: '—' },
  { id: '12', name: 'Startup Risk Preference Config', label: 'StartupRiskPreference', type: 'MASTER', lastRun: '—' },
  { id: '13', name: 'Technology Stack Config', label: 'TechnologyStack', type: 'MASTER', lastRun: '—' },
  { id: '14', name: 'Core Expertise Config', label: 'CoreExpertise', type: 'MASTER', lastRun: '—' },
  { id: '15', name: 'Domain Expertise Config', label: 'DomainExpertise', type: 'MASTER', lastRun: '—' },
  { id: '16', name: 'Ignite Filters Config', label: 'IgniteFilters', type: 'FILTER', lastRun: '—' },
  { id: '17', name: 'Accelerate Filters Config', label: 'AccelerateFilters', type: 'FILTER', lastRun: '—' },
  { id: '18', name: 'Dominate Filters Config', label: 'DominateFilters', type: 'FILTER', lastRun: '—' },
  { id: '19', name: 'Leadership Config', label: 'LeadershipConfigs', type: 'SCORE', lastRun: '—' },
  { id: '20', name: 'Architecture Config', label: 'ArchitectureConfigs', type: 'SCORE', lastRun: '—' },
  { id: '21', name: 'Business Impact Config', label: 'BusinessImpactConfigs', type: 'SCORE', lastRun: '—' },
  { id: '22', name: 'Achievement Config', label: 'AchievementConfigs', type: 'SCORE', lastRun: '—' },
  { id: '23', name: 'Admin Menus', label: 'AdminMenus', type: 'MENU', lastRun: '—' },
];

const TYPE_OPTIONS = ['ALL', 'MASTER', 'FILTER', 'SCORE', 'MENU'] as const;

const TYPE_STYLES: Record<ConfigType, string> = {
  MASTER: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
  FILTER: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
  SCORE: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  MENU: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
};

const TYPE_DESCRIPTIONS: Record<ConfigType, string> = {
  MASTER: 'Seeds master option values',
  FILTER: 'Registers candidate filter rules',
  SCORE: 'Registers score configuration',
  MENU: 'Registers admin menu entries',
};

const prettifyType = (value?: string) =>
  value ? value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const formatDate = (value?: string | null) => {
  if (!value || value === '—') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTime = (value?: string) => {
  if (!value) return '--:--:--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--:--:--';
  return (
    String(date.getHours()).padStart(2, '0') +
    ':' +
    String(date.getMinutes()).padStart(2, '0') +
    ':' +
    String(date.getSeconds()).padStart(2, '0')
  );
};

const fallbackLogs = (label: string, status: RunStatus, message?: string): RunLog[] => {
  const now = new Date().toISOString();
  const lines: Array<[string, string]> = [
    ['INFO', `Initializing ${label} config...`],
    ['INFO', 'Loading source configuration'],
    ['INFO', 'Checking for existing records...'],
  ];
  if (status === 'SUCCESS') {
    lines.push(['INFO', `Successfully executed ${label}.`]);
  } else {
    lines.push(['ERROR', `Execution failed: ${message || 'Unknown error'}`]);
    lines.push(['ERROR', `Aborted ${label}.`]);
  }
  return lines.map(([level, message]) => ({ timestamp: now, level, message }));
};

const logLevelColor = (level?: string) => {
  switch ((level || 'INFO').toUpperCase()) {
    case 'ERROR':
      return 'text-rose-400';
    case 'WARN':
      return 'text-yellow-400';
    case 'INFO':
    default:
      return 'text-emerald-400';
  }
};

const loadHistory = (): Record<string, RunRecord[]> => {
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.error('Failed to load run history', err);
    return {};
  }
};

const persistHistory = (history: Record<string, RunRecord[]>) => {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch (err) {
    console.error('Failed to persist run history', err);
  }
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

function JumpStartServiceComponent() {
  const [services, setServices] = useState<ServiceData[]>(SERVICES);
  const [history, setHistory] = useState<Record<string, RunRecord[]>>(loadHistory);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [type, setType] = useState<(typeof TYPE_OPTIONS)[number]>('ALL');
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [runTarget, setRunTarget] = useState<ServiceData | null>(null);
  const [running, setRunning] = useState(false);
  const [logTarget, setLogTarget] = useState<ServiceData | null>(null);
  const [logRun, setLogRun] = useState<RunRecord | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 350);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const lastRunFor = (serviceId: string) => {
    const runs = history[serviceId];
    return runs && runs.length > 0 ? runs[0].ranAt : '—';
  };

  const filteredServices = useCallback(() => {
    return services.filter((service) => {
      const matchesSearch =
        !debouncedSearch ||
        service.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        service.label.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchesType = type === 'ALL' || service.type === type;
      return matchesSearch && matchesType;
    });
  }, [services, debouncedSearch, type]);

  const resetFilters = () => {
    setSearch('');
    setType('ALL');
  };

  const hasActiveFilters = Boolean(search) || type !== 'ALL';

  const recordRun = (record: RunRecord) => {
    const next = {
      ...history,
      [record.id]: [record, ...(history[record.id] ?? [])].slice(0, MAX_HISTORY_PER_SERVICE),
    };
    setHistory(next);
    persistHistory(next);
    setServices((prev) => prev.map((s) => (s.id === record.id ? { ...s, lastRun: record.ranAt } : s)));
  };

  const toRunLog = (entry: JumpStartLogEntryDTO): RunLog => ({
    timestamp: entry.timestamp ?? new Date().toISOString(),
    level: entry.level ?? 'INFO',
    message: entry.message ?? '',
  });

  const handleRun = async () => {
    if (!runTarget) return;
    setRunning(true);
    setAlert(null);
    const startedAt = Date.now();
    const ranAt = new Date(startedAt).toISOString();
    let record: RunRecord | null = null;
    try {
      const response = await runJumpStartService(runTarget.label);
      const data = response.data as JumpStartServiceResponseDTO | null;
      if (response.success) {
        record = {
          id: runTarget.id,
          name: runTarget.name,
          label: runTarget.label,
          ranAt,
          inserted: data?.insertedCount ?? 0,
          skipped: data?.skippedCount ?? 0,
          durationMs: data?.durationMs ?? Date.now() - startedAt,
          status: data?.status === 'FAILED' ? 'FAILED' : 'SUCCESS',
          message: response.message,
          logs: data?.logs?.length ? data.logs.map(toRunLog) : fallbackLogs(runTarget.label, 'SUCCESS'),
        };
        setAlert({
          variant: record.status === 'SUCCESS' ? 'success' : 'error',
          title: record.status === 'SUCCESS' ? 'Success' : 'Run failed',
          message:
            record.status === 'SUCCESS'
              ? `"${runTarget.name}" executed successfully. ${record.inserted} inserted, ${record.skipped} skipped.`
              : response.message || 'Could not run the JumpStart service.',
          autoCloseMs: record.status === 'SUCCESS' ? 4000 : 6000,
          onClose: () => setAlert(null),
        });
      } else {
        record = {
          id: runTarget.id,
          name: runTarget.name,
          label: runTarget.label,
          ranAt,
          inserted: 0,
          skipped: 0,
          durationMs: Date.now() - startedAt,
          status: 'FAILED',
          message: response.message,
          logs: fallbackLogs(runTarget.label, 'FAILED', response.message),
        };
        setAlert({
          variant: 'error',
          title: 'Run failed',
          message: response.message || 'Could not run the JumpStart service.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to run JumpStart service', err);
      record = {
        id: runTarget.id,
        name: runTarget.name,
        label: runTarget.label,
        ranAt,
        inserted: 0,
        skipped: 0,
        durationMs: Date.now() - startedAt,
        status: 'FAILED',
        message: 'Unexpected system error',
        logs: fallbackLogs(runTarget.label, 'FAILED', 'Unexpected system error'),
      };
      setAlert({
        variant: 'error',
        title: 'System error',
        message: 'Something went wrong. Please try again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      if (record) {
        recordRun(record);
        if (record.status === 'SUCCESS') {
          setRunTarget(null);
        }
      }
      setRunning(false);
    }
  };

  const refresh = () => {
    setLoading(true);
    setError('');
    setAlert(null);
    setTimeout(() => setLoading(false), 350);
  };

  const openLogs = (service: ServiceData) => {
    setLogTarget(service);
    setLogRun(history[service.id]?.[0] ?? null);
  };

  const visibleServices = filteredServices();
  const masterCount = services.filter((s) => s.type === 'MASTER').length;
  const filterScoreCount = services.filter((s) => s.type === 'FILTER' || s.type === 'SCORE').length;
  const menuCount = services.filter((s) => s.type === 'MENU').length;
  const serviceRuns = logTarget ? (history[logTarget.id] ?? []) : [];
  const detailRun = logRun ?? serviceRuns[0] ?? null;

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Rocket size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
              Jumpstart Configuration Services
            </h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Run and manage configuration seeding services
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total Configs"
          value={services.length}
          icon={<Layers size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Master Configs"
          value={masterCount}
          icon={<Database size={16} />}
          accent="border-sky-500/30 bg-sky-500/10 text-sky-400"
        />
        <StatCard
          label="Filter & Score"
          value={filterScoreCount}
          icon={<SlidersHorizontal size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
        <StatCard
          label="Admin Menus"
          value={menuCount}
          icon={<ListChecks size={16} />}
          accent="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="relative md:col-span-2 xl:col-span-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search service name..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="relative">
            <Tag size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              value={type}
              onChange={(e) => setType(e.target.value as (typeof TYPE_OPTIONS)[number])}
              className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-8 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option} value={option} className="bg-slate-900 text-slate-100">
                  {option === 'ALL' ? 'All Config Types' : prettifyType(option)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          {TYPE_OPTIONS.map((option) => (
            <button
              key={option}
              onClick={() => setType(option)}
              className={`rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
                type === option
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:text-slate-200'
              }`}
            >
              {option === 'ALL' ? 'All Types' : prettifyType(option)}
            </button>
          ))}

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="ml-auto flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div key={idx} className="animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="mb-4 h-5 w-48 rounded bg-slate-800" />
              <div className="space-y-3">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-4 w-full rounded bg-slate-800/70" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : visibleServices.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <span className="mb-2 block text-2xl">🎯</span>
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">No config services found</p>
          {hasActiveFilters && (
            <button onClick={resetFilters} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-950 text-cyan-400">
                  <Rocket size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-black tracking-tight text-slate-100 md:text-base">Config Services</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {visibleServices.length} service{visibleServices.length === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3">Service</th>
                    <th className="px-5 py-3">Config Type</th>
                    <th className="px-5 py-3">Last Run</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleServices.map((service) => {
                    const lastRun = lastRunFor(service.id);
                    const lastRecord = history[service.id]?.[0];
                    return (
                      <tr
                        key={service.id}
                        className="border-b border-slate-800/60 transition last:border-0 hover:bg-slate-800/20"
                      >
                        <td className="px-5 py-4">
                          <p className="text-sm font-bold text-slate-100">{service.name}</p>
                          <p className="mt-0.5 text-[11px] text-slate-500">{TYPE_DESCRIPTIONS[service.type]}</p>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                              TYPE_STYLES[service.type] ?? 'border-slate-700 bg-slate-950 text-slate-400'
                            }`}
                          >
                            <Tag size={11} /> {prettifyType(service.type)}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                            <CalendarDays size={12} className="text-slate-500" />
                            {lastRun !== '—' ? (
                              <span className="text-emerald-400">{formatDate(lastRun)}</span>
                            ) : (
                              '—'
                            )}
                          </span>
                          {lastRecord && (
                            <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                              <CheckCircle2 size={11} className={lastRecord.status === 'SUCCESS' ? 'text-emerald-500' : 'text-rose-500'} />
                              {lastRecord.inserted ?? 0} ins · {lastRecord.skipped ?? 0} skip · {formatTime(lastRecord.ranAt)}
                            </p>
                          )}
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setRunTarget(service)}
                              disabled={running}
                              title="Run service"
                              className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-emerald-400 transition hover:border-emerald-500/40 disabled:opacity-50"
                            >
                              <Play size={12} /> Run
                            </button>
                            <button
                              onClick={() => openLogs(service)}
                              title="View logs"
                              className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
                            >
                              <FileText size={12} /> Logs
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Showing {visibleServices.length} of {services.length} JumpStart services
          </div>
        </motion.div>
      )}

      {/* Run confirmation */}
      {runTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !running && setRunTarget(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">Run JumpStart service?</h3>
                <p className="mt-1 text-sm text-slate-400">
                  This will seed the{' '}
                  <span className="font-bold text-slate-200">"{runTarget.name}"</span> configuration
                  ({runTarget.label}) and insert any missing records.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setRunTarget(null)}
                disabled={running}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRun}
                disabled={running}
                className="flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-emerald-400 disabled:opacity-50"
              >
                {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />}
                {running ? 'Running' : 'Run Service'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Logs modal */}
      {logTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => setLogTarget(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                  <FileText size={18} />
                </span>
                <div>
                  <h3 className="text-base font-black tracking-tight text-slate-100">System Logs</h3>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{logTarget.name}</p>
                </div>
              </div>
              <button
                onClick={() => setLogTarget(null)}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-800 bg-slate-950 text-slate-500 transition hover:text-slate-200"
              >
                <X size={15} />
              </button>
            </div>

            {serviceRuns.length === 0 ? (
              <div className="h-64 overflow-y-auto rounded-xl border border-dashed border-slate-700 bg-black/40 p-4 text-sm leading-relaxed text-slate-400">
                <p>No runs recorded yet for this service.</p>
                <p className="mt-1">Run the service to capture detailed execution logs.</p>
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
                {/* Run history */}
                <div className="flex w-full flex-col lg:w-2/5">
                  <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                    <History size={13} /> Run History ({serviceRuns.length})
                  </div>
                  <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                    {serviceRuns.map((run) => (
                      <button
                        key={run.ranAt}
                        onClick={() => setLogRun(run)}
                        className={`w-full rounded-xl border p-3 text-left transition ${
                          detailRun && detailRun.ranAt === run.ranAt
                            ? 'border-cyan-500/40 bg-cyan-500/10'
                            : 'border-slate-800 bg-slate-950 hover:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 text-[11px] font-bold text-slate-200">
                            <Clock size={11} className="text-slate-500" />
                            {formatDate(run.ranAt)}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                              run.status === 'SUCCESS'
                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                                : 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                            }`}
                          >
                            {run.status === 'SUCCESS' ? <CheckCircle2 size={9} /> : <AlertTriangle size={9} />}
                            {run.status}
                          </span>
                        </div>
                        <p className="mt-1 text-[10px] font-semibold text-slate-500">
                          {run.inserted ?? 0} inserted · {run.skipped ?? 0} skipped
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Run details */}
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                    <Tag size={13} /> Run Details
                  </div>
                  {detailRun && (
                    <>
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${
                            detailRun.status === 'SUCCESS'
                              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                              : 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                          }`}
                        >
                          {detailRun.status === 'SUCCESS' ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
                          {detailRun.status}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                          <Database size={10} className="text-emerald-400" /> {detailRun.inserted ?? 0} inserted
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                          <ListChecks size={10} className="text-slate-400" /> {detailRun.skipped ?? 0} skipped
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                          <Clock size={10} className="text-cyan-400" /> {((detailRun.durationMs ?? 0) / 1000).toFixed(2)}s
                        </span>
                      </div>

                      <div className="overflow-y-auto rounded-xl border border-slate-800 bg-black/50 p-4 font-mono text-sm leading-relaxed">
                        {detailRun.logs && detailRun.logs.length > 0 ? (
                          detailRun.logs.map((entry, idx) => (
                            <p key={`${entry.timestamp}-${idx}`} className={logLevelColor(entry.level)}>
                              <span className="text-slate-500">[{formatTime(entry.timestamp)}]</span>{' '}
                              {entry.message}
                            </p>
                          ))
                        ) : (
                          <p className="text-slate-500">No log entries captured for this run.</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setLogTarget(null)}
                className="rounded-xl border border-slate-800 bg-slate-950 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-300 transition hover:text-slate-100"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default JumpStartServiceComponent;